import { Controller } from "@hotwired/stimulus";

// Find-or-create multi-select for ingredient tags (master ingredient form).
export default class extends Controller {
  static targets = ["searchInput", "dropdown", "selectedList", "errorMessage"];

  static values = {
    searchUrl: String,
    createUrl: String,
    initialTags: { type: Array, default: [] },
  };

  connect() {
    this.selected = new Map();
    this.highlightIndex = -1;
    this.results = [];

    this.initialTagsValue.forEach((tag) => {
      this.addTag(tag.id, tag.name, { render: false });
    });
    this.renderSelected();

    this.debouncedSearch = this.debounce(() => this.performSearch(), 200);
    this.clickOutside = this.onClickOutside.bind(this);
    document.addEventListener("click", this.clickOutside);
  }

  disconnect() {
    document.removeEventListener("click", this.clickOutside);
  }

  onSearchInput() {
    this.highlightIndex = -1;
    this.debouncedSearch();
  }

  async performSearch() {
    const query = this.searchInputTarget.value.trim();
    if (query.length < 1) {
      this.results = [];
      this.renderDropdown();
      return;
    }

    try {
      const url = new URL(this.searchUrlValue, window.location.origin);
      url.searchParams.set("q", query);
      const response = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      this.results = await response.json();
      this.renderDropdown();
    } catch (err) {
      this.showError(err.message);
    }
  }

  openDropdown() {
    if (this.searchInputTarget.value.trim().length > 0) {
      this.dropdownTarget.classList.remove("hidden");
    }
  }

  closeDropdown() {
    this.dropdownTarget.classList.add("hidden");
    this.highlightIndex = -1;
  }

  keydown(event) {
    if (event.key === "Escape") {
      this.closeDropdown();
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const option = this.dropdownTarget.querySelector("[data-highlighted='true']");
      if (option) {
        option.click();
      } else {
        this.addFromQuery();
      }
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const options = [...this.dropdownTarget.querySelectorAll("[data-option]")];
      if (options.length === 0) return;

      if (event.key === "ArrowDown") {
        this.highlightIndex = Math.min(this.highlightIndex + 1, options.length - 1);
      } else {
        this.highlightIndex = Math.max(this.highlightIndex - 1, 0);
      }

      options.forEach((el, i) => {
        el.dataset.highlighted = i === this.highlightIndex ? "true" : "false";
        el.classList.toggle("bg-base-200", i === this.highlightIndex);
      });
    }
  }

  selectOption(event) {
    const { id, name, create } = event.currentTarget.dataset;
    if (create === "true") {
      this.createAndSelect(name);
    } else {
      this.addTag(Number(id), name);
    }
    this.searchInputTarget.value = "";
    this.closeDropdown();
    this.results = [];
  }

  async addFromQuery() {
    const query = this.searchInputTarget.value.trim();
    if (!query) return;

    const exact = this.results.find(
      (tag) => tag.name.toLowerCase() === query.toLowerCase()
    );
    if (exact) {
      this.addTag(exact.id, exact.name);
      this.searchInputTarget.value = "";
      this.closeDropdown();
      return;
    }

    await this.createAndSelect(query);
  }

  async createAndSelect(name) {
    this.clearError();
    try {
      const payload = await this.postJSON(this.createUrlValue, {
        ingredient_tag: { name },
      });
      this.addTag(payload.id, payload.name);
      this.searchInputTarget.value = "";
      this.closeDropdown();
    } catch (err) {
      this.showError(err.message);
    }
  }

  remove(event) {
    const id = Number(event.currentTarget.dataset.id);
    this.selected.delete(id);
    this.renderSelected();
  }

  addTag(id, name, options = {}) {
    const { render = true } = options;
    if (this.selected.has(id)) {
      if (render) this.renderSelected();
      return;
    }
    this.selected.set(id, name);
    if (render) this.renderSelected();
    this.clearError();
  }

  renderSelected() {
    this.selectedListTarget.replaceChildren();

    if (this.selected.size === 0) {
      const hint = document.createElement("p");
      hint.className = "text-sm text-base-content/50";
      hint.textContent = "Aucune étiquette sélectionnée.";
      this.selectedListTarget.appendChild(hint);
      return;
    }

    this.selected.forEach((name, id) => {
      const chip = document.createElement("span");
      chip.className = "badge badge-primary badge-lg gap-1 pr-1";

      const label = document.createElement("span");
      label.textContent = name;

      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "btn btn-ghost btn-xs btn-circle text-primary-content";
      removeBtn.dataset.id = id;
      removeBtn.dataset.action = "click->ingredient-tag-picker#remove";
      removeBtn.setAttribute("aria-label", `Retirer ${name}`);
      removeBtn.textContent = "×";

      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = "master_ingredient[ingredient_tag_ids][]";
      hidden.value = id;

      chip.appendChild(label);
      chip.appendChild(removeBtn);
      chip.appendChild(hidden);
      this.selectedListTarget.appendChild(chip);
    });
  }

  renderDropdown() {
    const query = this.searchInputTarget.value.trim();
    const list = this.dropdownTarget;
    list.replaceChildren();

    if (query.length < 1) {
      this.closeDropdown();
      return;
    }

    const available = this.results.filter((tag) => !this.selected.has(tag.id));
    const exactMatch = available.some(
      (tag) => tag.name.toLowerCase() === query.toLowerCase()
    );

    available.forEach((tag, index) => {
      list.appendChild(this.buildOption({ id: tag.id, name: tag.name, index }));
    });

    if (!exactMatch) {
      list.appendChild(
        this.buildOption({
          id: null,
          name: query,
          create: true,
          index: available.length,
        })
      );
    }

    if (list.children.length === 0) {
      const empty = document.createElement("li");
      empty.className = "px-3 py-2 text-sm text-base-content/60";
      empty.textContent = "Aucune étiquette trouvée.";
      list.appendChild(empty);
    }

    list.classList.remove("hidden");
    this.highlightIndex = list.querySelector("[data-option]") ? 0 : -1;
    this.updateHighlight();
  }

  buildOption({ id, name, create = false, index }) {
    const li = document.createElement("li");
    li.dataset.option = "true";
    li.dataset.highlighted = index === 0 ? "true" : "false";
    li.className =
      "cursor-pointer px-3 py-2 text-sm hover:bg-base-200 " +
      (index === 0 ? "bg-base-200" : "");

    if (create) {
      li.dataset.create = "true";
      li.dataset.name = name;
      li.dataset.action = "click->ingredient-tag-picker#selectOption";
      li.innerHTML = `<span class="text-primary font-medium">Créer « ${this.escapeHtml(name)} »</span>`;
    } else {
      li.dataset.id = id;
      li.dataset.name = name;
      li.dataset.action = "click->ingredient-tag-picker#selectOption";
      li.textContent = name;
    }

    return li;
  }

  updateHighlight() {
    const options = [...this.dropdownTarget.querySelectorAll("[data-option]")];
    options.forEach((el, i) => {
      el.dataset.highlighted = i === this.highlightIndex ? "true" : "false";
      el.classList.toggle("bg-base-200", i === this.highlightIndex);
    });
  }

  onClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown();
    }
  }

  async postJSON(url, body) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const response = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": token || "",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try {
        const data = await response.json();
        if (Array.isArray(data.errors)) message = data.errors.join(", ");
      } catch (_) {}
      throw new Error(message);
    }

    return response.json();
  }

  debounce(fn, ms) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), ms);
    };
  }

  escapeHtml(text) {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  showError(message) {
    if (!this.hasErrorMessageTarget) return;
    this.errorMessageTarget.textContent = message;
    this.errorMessageTarget.classList.remove("hidden");
  }

  clearError() {
    if (!this.hasErrorMessageTarget) return;
    this.errorMessageTarget.textContent = "";
    this.errorMessageTarget.classList.add("hidden");
  }
}
